import { Box, Button, ButtonBase, MenuItem, Popover, Stack, Typography } from "@mui/material";
import { useState } from "react";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import TruxSimpleSelectField from "src/components/inputs/trux-simple-select-field";
import { RentalTire } from "src/models";
import { RENTAL_TIRE_RATINGS, rentalTrailerType } from "src/constants";
import { TRAILER_SLOTS, slotRowsForVehicleType } from "src/utils/tpms-utils";
import { Colors } from "src/theme/palette";

export function emptyTire(): RentalTire {
  return { brand: "", value: 0, serial: "", conditionType: "", condition: "", comment: "" };
}

// brakes are recorded per axle end, not per wheel — 4 positions, 2 per axle
export const BRAKE_SLOT_LABELS = ["L1", "R1", "L2", "R2"];
export const BRAKE_SLOT_ROWS = [
  [0, 1],
  [2, 3],
];

// trucks carry their own wheel layouts in the type registry; anything else
// uses the trailer layout
export function tireSlotLabelsForType(trailerType?: string) {
  return rentalTrailerType(trailerType)?.tireSlotLabels || TRAILER_SLOTS.map((slot) => slot.label);
}

export function tireSlotRowsForType(trailerType?: string) {
  return rentalTrailerType(trailerType)?.tireSlotRows;
}

export function brakeSlotLabelsForType(trailerType?: string) {
  return rentalTrailerType(trailerType)?.brakeSlotLabels || BRAKE_SLOT_LABELS;
}

export function brakeSlotRowsForType(trailerType?: string) {
  return rentalTrailerType(trailerType)?.brakeSlotRows || BRAKE_SLOT_ROWS;
}

export function emptyBrakeRows(trailerType?: string): RentalTire[] {
  return Array.from({ length: brakeSlotLabelsForType(trailerType).length }, () => emptyTire());
}

export function tireFilled(entry: RentalTire) {
  return entry.brand !== "" || entry.serial !== "" || entry.condition !== "" || entry.value !== 0;
}

export function tireConditionLabel(entry: RentalTire) {
  if (entry.conditionType === "depth" && entry.condition) {
    return `${entry.condition} mm`;
  }
  if (entry.conditionType === "thirtyseconds" && entry.condition) {
    return `${entry.condition}/32"`;
  }
  if (entry.conditionType === "percentage" && entry.condition) {
    return `${entry.condition}%`;
  }
  if (entry.condition) {
    return entry.condition;
  }
  // rows recorded before conditions existed only carry a pressure reading
  if (entry.value !== 0) {
    return `${entry.value} psi`;
  }
  return "";
}

function tireColor(entry: RentalTire) {
  if (entry.condition === "Fair") {
    return Colors.orange;
  }
  if (entry.condition === "Poor") {
    return "error.main";
  }
  return "success.main";
}

type WheelProps = {
  slotIndex: number;
  slotLabel: string;
  entry: RentalTire;
  selected: boolean;
  editable: boolean;
  onTap: (slotIndex: number, anchor: HTMLElement) => void;
};

function Wheel({ slotIndex, slotLabel, entry, selected, editable, onTap }: WheelProps) {
  const filled = tireFilled(entry);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={0.25}>
      <ButtonBase
        disabled={!editable}
        onClick={(event) => onTap(slotIndex, event.currentTarget)}
        sx={{
          width: 58,
          height: 68,
          borderRadius: 1.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          transition: "filter 0.15s, opacity 0.15s",
          ...(filled
            ? {
                bgcolor: tireColor(entry),
                color: "common.white",
              }
            : {
                border: "2px dashed",
                borderColor: "divider",
                color: "text.disabled",
              }),
          ...(selected && {
            outline: "3px solid",
            outlineColor: "primary.main",
            outlineOffset: "2px",
          }),
        }}
      >
        {filled ? (
          <>
            <Typography variant="subtitle2" lineHeight={1.2} noWrap maxWidth={52}>
              {tireConditionLabel(entry)}
            </Typography>
            {entry.brand && (
              <Typography variant="caption" lineHeight={1.2} noWrap maxWidth={52}>
                {entry.brand}
              </Typography>
            )}
            {entry.serial && (
              <Typography variant="caption" lineHeight={1.2} noWrap maxWidth={52} sx={{ opacity: 0.8 }}>
                {entry.serial}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption">Tap</Typography>
        )}
      </ButtonBase>
      <Typography variant="caption" color="text.secondary">
        {slotLabel || slotIndex + 1}
      </Typography>
    </Box>
  );
}

type ConditionInputProps = {
  conditionType: string;
  entry: RentalTire;
  editable: boolean;
  onCondition: (condition: string) => void;
};

function ConditionInput({ conditionType, entry, editable, onCondition }: ConditionInputProps) {
  if (!editable) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          Condition
        </Typography>
        <Typography>{tireConditionLabel(entry)}</Typography>
      </Box>
    );
  }
  if (conditionType === "depth") {
    return (
      <TruxSimpleTextField
        label="Tread Depth (mm)"
        value={entry.condition}
        onChange={(condition) => onCondition(condition)}
      />
    );
  }
  if (conditionType === "thirtyseconds") {
    return (
      <TruxSimpleTextField
        label='Tread Depth (32nds ")'
        value={entry.condition}
        onChange={(condition) => onCondition(condition)}
      />
    );
  }
  if (conditionType === "percentage") {
    return (
      <TruxSimpleTextField
        label="Tread Remaining (%)"
        value={entry.condition}
        onChange={(condition) => onCondition(condition)}
      />
    );
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Condition
      </Typography>
      <TruxSimpleSelectField value={entry.condition} onChange={(condition) => onCondition(condition)}>
        <MenuItem value="">Please Select</MenuItem>
        {RENTAL_TIRE_RATINGS.map((rating) => (
          <MenuItem key={rating} value={rating}>
            {rating}
          </MenuItem>
        ))}
      </TruxSimpleSelectField>
    </Box>
  );
}

export type RentalWheelDiagramProps = {
  rows: RentalTire[];
  conditionType: string;
  editable: boolean;
  onChange: (rows: RentalTire[]) => void;
  // brakes reuse the tire structure but have no serial number and use the
  // 4-position axle-end layout instead of the 8 wheel slots
  itemLabel?: string;
  hideSerial?: boolean;
  slotLabels?: string[];
  slotRows?: number[][];
};

export function RentalWheelDiagram({ rows: rawRows, conditionType, editable, onChange, itemLabel = "Tire", hideSerial = false, slotLabels, slotRows }: RentalWheelDiagramProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openSlot, setOpenSlot] = useState<number | null>(null);

  const labels = slotLabels || TRAILER_SLOTS.map((slot) => slot.label);

  // a not-yet-recorded list arrives as [] — pad to one entry per slot so
  // tapping any position edits a real row
  const rows = Array.from({ length: labels.length }, (_, i) => rawRows[i] || emptyTire());

  const axleRows = slotRows || slotRowsForVehicleType("trailer");

  const onWheelTap = (slotIndex: number, anchor: HTMLElement) => {
    setOpenSlot(slotIndex);
    setAnchorEl(anchor);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setOpenSlot(null);
  };

  const updateOpenSlot = (updater: (entry: RentalTire) => RentalTire) => {
    if (openSlot === null) {
      return;
    }
    onChange(
      rows.map((entry, index) =>
        index === openSlot ? { ...updater(entry), conditionType } : entry
      )
    );
  };

  const openEntry = openSlot !== null ? rows[openSlot] : null;

  const renderWheel = (slotIndex: number) => (
    <Wheel
      key={slotIndex}
      slotIndex={slotIndex}
      slotLabel={labels[slotIndex]}
      entry={rows[slotIndex] || emptyTire()}
      selected={openSlot === slotIndex}
      editable={editable}
      onTap={onWheelTap}
    />
  );

  return (
    <>
      <Stack spacing={3} sx={{ py: 1 }}>
        {axleRows.map((axleRow, rowIndex) => {
          const leftHalf = axleRow.slice(0, axleRow.length / 2);
          const rightHalf = axleRow.slice(axleRow.length / 2);
          return (
            <Box key={rowIndex} display="flex" justifyContent="center" gap={5}>
              <Stack direction="row" spacing={1.5}>
                {leftHalf.map(renderWheel)}
              </Stack>
              <Stack direction="row" spacing={1.5}>
                {rightHalf.map(renderWheel)}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Popover
        open={!!anchorEl && openSlot !== null}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {openEntry && openSlot !== null && (
          <Stack gap={1.5} p={2} width={250}>
            <Typography variant="subtitle2">
              {`${itemLabel} ${labels[openSlot] || openSlot + 1}`}
            </Typography>
            <TruxSimpleTextField
              label="Brand"
              value={openEntry.brand}
              disabled={!editable}
              onChange={(brand) => updateOpenSlot((entry) => ({ ...entry, brand }))}
            />
            {!hideSerial && (
              <TruxSimpleTextField
                label="Serial"
                value={openEntry.serial}
                disabled={!editable}
                onChange={(serial) => updateOpenSlot((entry) => ({ ...entry, serial }))}
              />
            )}
            <ConditionInput
              conditionType={conditionType}
              entry={openEntry}
              editable={editable}
              onCondition={(condition) => updateOpenSlot((entry) => ({ ...entry, condition }))}
            />
            <TruxSimpleTextField
              label="Comment"
              value={openEntry.comment}
              disabled={!editable}
              onChange={(comment) => updateOpenSlot((entry) => ({ ...entry, comment }))}
            />
            <Stack direction="row" justifyContent="space-between">
              {editable ? (
                <Button size="small" color="error" onClick={() => updateOpenSlot(() => emptyTire())}>
                  Clear
                </Button>
              ) : (
                <Box />
              )}
              <Button size="small" variant="contained" onClick={closePopover}>
                Done
              </Button>
            </Stack>
          </Stack>
        )}
      </Popover>
    </>
  );
}
